import type { Importance, IssueStage, NormalizedIssue } from "@common/types/issue";

const KNOWN_IMPORTANCES: ReadonlySet<string> = new Set(["High", "Medium", "Low", "Draft"]);

const STATUS_TO_STAGE: Record<string, IssueStage> = {
  QUEUED: "queued",
  DETECTION: "detection",
  IN_PROGRESS: "in-progress",
  REMEDY: "remedy",
  DONE: "done",
  ERROR: "error",
  SKIPPED: "skipped",
};

/**
 * Resolves the raw status string off a rule entry. Backend may serialize as
 * `r.status` (string), `r.status.path` (nested object), or — for the partial
 * sub-list shape consumed by RuleDetailsPage — `r.rule.status`. Preserve all
 * three forms.
 */
export function resolveStatus(entry: {
  status?: unknown;
  rule?: { status?: unknown };
}): string | undefined {
  const direct = entry.status as { path?: string } | string | undefined;
  if (direct && typeof direct === "object" && typeof direct.path === "string") {
    return direct.path;
  }
  if (typeof direct === "string") return direct;
  const fromRule = entry.rule?.status;
  return typeof fromRule === "string" ? fromRule : undefined;
}

function coerceImportance(raw: string | undefined): Importance {
  if (raw && KNOWN_IMPORTANCES.has(raw)) return raw as Importance;
  if (raw && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[normalizeIssue] Unknown importance value "${raw}" — coercing to Draft`);
  }
  return "Draft";
}

export function statusToStage(status: string | undefined): IssueStage {
  if (!status) return "queued";
  return STATUS_TO_STAGE[status] ?? "queued";
}

/**
 * Reads responded/conceded flags from both top-level (`r.X`) and nested
 * (`r.rule.X`) — backend writes to either depending on the code path.
 */
function readFlag(entry: unknown, key: "rule_responded" | "rule_conceded"): boolean {
  if (!entry || typeof entry !== "object") return false;
  const e = entry as Record<string, unknown> & { rule?: Record<string, unknown> };
  if (typeof e[key] === "boolean") return e[key] as boolean;
  if (e.rule && typeof e.rule[key] === "boolean") return e.rule[key] as boolean;
  return false;
}

/**
 * Maps the backend `reviewReport` shape (and its partial variant used in
 * RuleDetailsPage's section sub-list) to the normalized issue shape consumed
 * by `IssueCard`, `IssueList`, `DeviationScoreBar`, and the Stage-2 viewer.
 */
export function reviewReportToNormalizedIssues(
  report: ReadonlyArray<Record<string, any>> | null | undefined,
): NormalizedIssue[] {
  if (!report) return [];
  const seen = new Set<string>();
  const out: NormalizedIssue[] = [];
  for (let i = 0; i < report.length; i++) {
    const r = report[i];
    const rule = (r.rule ?? {}) as Record<string, unknown>;
    const ruleUuid = typeof rule.uuid === "string" ? rule.uuid : undefined;
    if (ruleUuid) {
      if (seen.has(ruleUuid)) continue;
      seen.add(ruleUuid);
    }
    out.push({
      ruleUuid,
      index: typeof r.index === "number" ? r.index : i,
      name: typeof rule.name === "string" ? rule.name : "",
      description: typeof rule.description === "string" ? rule.description : undefined,
      importance: coerceImportance(typeof r.importance === "string" ? r.importance : (rule.importance as string | undefined)),
      match: r.match === true,
      stage: statusToStage(resolveStatus(r)),
      responded: readFlag(r, "rule_responded"),
      conceded: readFlag(r, "rule_conceded"),
      error: typeof rule.error === "string" ? rule.error : undefined,
    });
  }
  return out;
}
